import type { Address, Hex } from "viem";
import type { Intent } from "@woodpecker/protocol";

export type ChainErrorName =
  | "CapExceeded"
  | "NonceUsed"
  | "BadSignature"
  | "NotService"
  | "PayToNotService"
  | "ActualAboveMax"
  | "ZeroAddress"
  | "ERC20InsufficientBalance"
  | "ERC20InsufficientAllowance"
  | "InsufficientMintAllowance"
  | "ExchangeDisabled"
  | "BadCredits"
  | "OwnableUnauthorizedAccount"
  | "HandleExists"
  | "HandleTaken"
  | "BadName"
  | "BadUrl"
  | "NameTaken"
  | "NoHandle"
  | "NotAServiceToken"
  | "NotAuthorized"
  | "UnknownToken"
  | "BadHash"
  | "UnknownEntry"
  | "InsufficientFunds"
  | "Unknown";

/** A revert, with the contract's error name and its arguments (both the simulator and the RPC client throw it). */
export class ChainError extends Error {
  constructor(
    public readonly errorName: ChainErrorName,
    public readonly args: ReadonlyArray<unknown> = [],
    message?: string,
  ) {
    super(message ?? `${errorName}(${args.map(String).join(", ")})`);
    this.name = "ChainError";
  }
}

export interface Authorization {
  cap: bigint;
  used: bigint;
}

export interface TokenInfo {
  address: Address;
  name: string;
  symbol: string;
  decimals: number;
  owner: Address;
  service: Address;
  rate: bigint;
  totalSupply: bigint;
}

export interface DirectoryEntry {
  token: Address;
  owner: Address;
  handleName: string;
  addr4: string; // 4 hex chars
  serviceName: string;
  url: string;
  /** keccak256 of the service's canonical /pricing, committed by the owner; ZERO_HASH = not committed (issue #3 item 11). */
  pricingHash: Hex;
  registeredAt: number;
  updatedAt: number;
  active: boolean;
}

/** One entry of the ExtensionDirectory (D70): the url of the published version and the sha256 of its manifest.json. */
export interface ExtensionEntry {
  owner: Address;
  name: string;
  url: string;
  hash: Hex;
  updatedAt: number;
  active: boolean;
}

export interface SpentEvent {
  client: Address;
  identity: Address;
  token: Address;
  payTo: Address;
  actual: bigint;
  nonce: Hex;
  credits: bigint;
  tx: Hex;
  block: number;
}

export interface ExchangedEvent {
  token: Address;
  from: Address;
  to: Address;
  credits: bigint;
  tokens: bigint;
  tx: Hex;
}

export interface Tx {
  tx: Hex;
  block: number;
}

/** A settle's transaction with the credits of its Spent event (the wpCredit paid for a shortfall, D67). */
export interface SettleResult extends Tx {
  credits: bigint;
}

/** A one-off scan: from this block to the head, no cursor (the lookup of one settle by its nonce). */
export interface ScanOptions {
  fromBlock?: bigint;
}

export interface ChainAddresses {
  chainId: number;
  contractA: Address;
  wpCredit: Address;
  directory: Address;
  /** The ExtensionDirectory (D70); absent on a stack deployed before it existed: the extension reads then throw. */
  extensionDirectory?: Address;
}

/** Views: no gas. */
export interface ChainReader {
  readonly addresses: ChainAddresses;
  authorizationOf(client: Address, identity: Address, token: Address): Promise<Authorization>;
  remaining(client: Address, identity: Address, token: Address): Promise<bigint>;
  tokensOf(client: Address, identity: Address): Promise<Address[]>;
  nonceUsed(identity: Address, nonce: Hex): Promise<boolean>;
  balanceOf(token: Address, account: Address): Promise<bigint>;
  allowance(token: Address, owner: Address, spender: Address): Promise<bigint>;
  tokenInfo(token: Address): Promise<TokenInfo>;
  mintAllowance(token: Address, minter: Address): Promise<bigint>;
  ethBalance(account: Address): Promise<bigint>;
  /** The chain's current gas price in wei (eth_gasPrice): what a settle costs with SETTLE_GAS. */
  gasPrice(): Promise<bigint>;
  dirHandleOf(owner: Address): Promise<{ name: string; addr4: string; exists: boolean }>;
  dirEntry(token: Address): Promise<DirectoryEntry>;
  dirTokensOf(owner: Address): Promise<Address[]>;
  dirCount(): Promise<number>;
  dirEntryAt(index: number): Promise<DirectoryEntry>;
  dirResolve(handleName: string, addr4: string, serviceName: string): Promise<Address | null>;
  /** The ExtensionDirectory's views (D70): one entry, a wallet's entries, the whole directory in publish order. */
  extEntry(owner: Address, name: string): Promise<ExtensionEntry>;
  extEntriesOf(owner: Address): Promise<ExtensionEntry[]>;
  extCount(): Promise<number>;
  extEntryAt(index: number): Promise<ExtensionEntry>;
  spentEvents(
    filter: {
      token?: Address;
      client?: Address;
      identity?: Address;
      payTo?: Address;
    },
    opts?: ScanOptions,
  ): Promise<SpentEvent[]>;
  exchangedEvents(token: Address): Promise<ExchangedEvent[]>;
  /** The chain's head block. */
  blockNumber(): Promise<bigint>;
}

/** Writes: every call is sent from a key the client holds (the wallet, a submitter, Woodpecker). */
export interface ChainWriter extends ChainReader {
  authorize(from: Address, identity: Address, token: Address, cap: bigint): Promise<Tx>;
  multicallAuthorize(from: Address, calls: Array<{ identity: Address; token: Address; cap: bigint }>): Promise<Tx>;
  settle(from: Address, intent: Intent, signature: Hex, actual: bigint): Promise<SettleResult>;
  approve(from: Address, token: Address, spender: Address, amount: bigint): Promise<Tx>;
  transfer(from: Address, token: Address, to: Address, amount: bigint): Promise<Tx>;
  exchange(from: Address, token: Address, credits: bigint, to: Address): Promise<Tx & { tokens: bigint }>;
  mint(from: Address, token: Address, to: Address, amount: bigint): Promise<Tx>;
  setRate(from: Address, token: Address, rate: bigint): Promise<Tx>;
  setService(from: Address, token: Address, payTo: Address): Promise<Tx>;
  setMintAllowance(from: Address, token: Address, minter: Address, allowance: bigint): Promise<Tx>;
  deployToken(from: Address, name: string, symbol: string, rate: bigint): Promise<Tx & { address: Address }>;
  claim(from: Address, name: string): Promise<Tx>;
  set(from: Address, token: Address, serviceName: string, url: string): Promise<Tx>;
  /** The token's owner commits the hash of the service's price document; ZERO_HASH withdraws it. */
  setPricingHash(from: Address, token: Address, pricingHash: Hex): Promise<Tx>;
  clear(from: Address, token: Address): Promise<Tx>;
  /** The owner wallet publishes a version of its extension (the url and the sha256 of manifest.json) or retires it (D70, D71). */
  publish(from: Address, name: string, url: string, hash: Hex): Promise<Tx>;
  unpublish(from: Address, name: string): Promise<Tx>;
  sendEth(from: Address, to: Address, amount: bigint): Promise<Tx>;
  wpMint(from: Address, to: Address, amount: bigint): Promise<Tx>;
}

export const MAX_UINT256 = (1n << 256n) - 1n;
/** A directory entry without a committed price document. */
export const ZERO_HASH: Hex = `0x${"0".repeat(64)}`;
